import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { SiteExp } from '@groma-dev/scaffold';
import { ToastMode, useDefineMessages, useToasts } from '@groma-dev/blueprint';
import { useIntl } from 'react-intl';
import { useAppStore, useDataStore, useOnboardingStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { FirebaseEvents } from '../../utils/Constants';
import { buildUrlWithParams } from '../../utils/WebHelper';
import { getOnboardingResumeRoute, redirectToPhoneVerification } from '../../utils/OnboardingHelper';
import { OnboardingRoutes } from '../../../core/routes/OnboardingRoutes';
import { APIResponse } from '../../../core/apiclient/BaseAPIClient';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';

const AuthRouter = observer(({ children }: { children: ReactElement }) => {
  const dataStore = useDataStore();
  const onboardingStore = useOnboardingStore();
  const { firebase } = useAppStore();
  const { pathname, search } = useLocation();
  const { formatMessage } = useIntl();
  const { addToast } = useToasts();
  const messages = useDefineMessages();
  const { userState, appState } = dataStore;
  const { state: onboardingState } = onboardingStore;
  const { Settings, Pages } = AppConfig;
  const { Authentication, Server, Domains, Metrics } = Settings;
  const { enabled: HomeEnabled } = Pages.Home;
  const { enabled: requireAuthentication, cookie } = Authentication;
  const { name: authCookieName } = cookie;
  const { enabled: protectionEnabled, protectionKey, cookieName: appCookieName } = Server.passwordProtection;
  const authCookiePresent = Cookies.get(authCookieName);
  const appPasswordMatch = Cookies.get(appCookieName) === protectionKey;

  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [initialized, setInitialized] = useState<boolean>(false);

  const firePageViewEvent = () => {
    if (Metrics.Firebase.enabled) {
      if (currentLocation !== pathname) {
        setCurrentLocation(pathname);
        firebase.fireEvent(FirebaseEvents.PAGE_VIEW, { path: pathname });
      }
    }
  };

  if (protectionEnabled && !appPasswordMatch && pathname !== AppRoutes.Auth) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);

    return <Navigate to={`${buildUrlWithParams(AppRoutes.Auth)}?return_url=${returnUrl}`} replace />;
  } else if (protectionEnabled && appPasswordMatch && pathname === AppRoutes.Auth) {
    return <Navigate to={buildUrlWithParams(AppRoutes.root)} replace />;
  }

  if ((protectionEnabled && appPasswordMatch) || !protectionEnabled) {
    if (requireAuthentication && !authCookiePresent) {
      const returnUrl = encodeURIComponent(`${pathname}${search}`);
      window.location.href = `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.INVESTORS}`;
      return null;
    }

    if (!HomeEnabled && pathname === AppRoutes.root) {
      return <Navigate to={buildUrlWithParams(AppRoutes.Overview)} replace />;
    }
  }

  if (authCookiePresent && !userState.user && !userState.fetchingUser && !initialized) {
    dataStore
      .fetchUser()
      .then((res: APIResponse) => {
        if (res.error) {
          setTimeout(() => addToast(res.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR), 100);
        }
      })
      .catch(e => handleErrorLogging(e));
  }

  if (userState.user && !userState.user.is_staff) {
    if (!onboardingState.progressFetched && !onboardingState.isFetchingProgress) {
      onboardingStore.fetchOnboardingProgress().catch(e => handleErrorLogging(e));
      return null;
    }

    if (onboardingState.isFetchingProgress || !onboardingState.progressFetched) {
      return null;
    }

    if (onboardingState.progressFetched && onboardingState.progress) {
      const result = getOnboardingResumeRoute(onboardingState.progress, userState.user);

      if (result?.type === 'phone_verification') {
        return redirectToPhoneVerification(pathname, search);
      }

      if (result?.type === 'route') {
        return <Navigate to={buildUrlWithParams(result.path)} replace />;
      }
    } else if (onboardingState.progressFetched && !onboardingState.progress) {
      if (!userState.user.email_verified) {
        return <Navigate to={buildUrlWithParams(OnboardingRoutes.VerifyEmail)} replace />;
      }
      if (!userState.user.phone_number_verified) {
        return redirectToPhoneVerification(pathname, search);
      }
    }
  }

  firePageViewEvent();
  if (!requireAuthentication && !authCookiePresent) dataStore.setAppInitialized(true);
  if (!initialized) setInitialized(true);

  if (
    appState.initialized &&
    appState.nonREITAccount &&
    ![AppRoutes.Settings, AppRoutes.Documents, AppRoutes.Help].includes(pathname)
  ) {
    return <Navigate to={AppRoutes.Documents} replace />;
  }

  return children;
});

export default AuthRouter;
